import React, { useEffect } from 'react';
import { formatRelativeTime, formatDateUser, formatDateTimeUser } from '../../utils/dateUtils';
import { getTaskStatusColor } from '../../utils/taskUtils';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import './TaskModals.css';

// Helper to convert hex colors to rgba with alpha for subtle header backgrounds
const hexToRgba = (hex, alpha = 0.15) => {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  let clean = hex.replace('#', '');
  if (clean.length === 3) {
    clean = clean.split('').map((c) => c + c).join('');
  }
  const bigint = parseInt(clean, 16);
  if (Number.isNaN(bigint)) return `rgba(0,0,0,${alpha})`;
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Lighten a hex color by mixing with white
const lightenHex = (hex, ratio = 0.3) => {
  if (!hex) return hex;
  let clean = hex.replace('#', '');
  if (clean.length === 3) {
    clean = clean.split('').map((c) => c + c).join('');
  }
  const num = parseInt(clean, 16);
  if (Number.isNaN(num)) return hex;
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const nr = Math.min(255, Math.round(r + (255 - r) * ratio));
  const ng = Math.min(255, Math.round(g + (255 - g) * ratio));
  const nb = Math.min(255, Math.round(b + (255 - b) * ratio));
  const toHex = (v) => v.toString(16).padStart(2, '0');
  return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
};

// Localized date formatters without year
const formatDateTimeNoYear = (value) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return String(value);
  }
};

const formatDateNoYear = (value) => {
  if (!value) return '';
  try {
    const d = new Date(value);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return String(value);
  }
};

const formatDuration = (start, end) => {
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return null;
  let diff = Math.max(0, endMs - startMs);
  const dayMs = 86400000;
  const hourMs = 3600000;
  const minMs = 60000;
  const days = Math.floor(diff / dayMs);
  diff -= days * dayMs;
  const hours = Math.floor(diff / hourMs);
  diff -= hours * hourMs;
  const mins = Math.floor(diff / minMs);
  const parts = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return parts.join(' ');
};

// Header helpers for due/resolution display
const formatHeaderTimeLabel = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
};

const getDueHeaderDisplay = (task) => {
  const due = task?.dueDate ? new Date(task.dueDate) : null;
  if (!due) return null;
  const now = new Date();
  const isTerminal = typeof task?.status === 'string' && ['resolved', 'rejected'].includes(task.status.toLowerCase());
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const today = startOfDay(now);
  const dueDay = startOfDay(due);
  const diffDays = Math.round((dueDay.getTime() - today.getTime()) / 86400000);
  const diffMs = Math.abs(due.getTime() - now.getTime());

  let label;
  if (diffMs < 60000) {
    label = 'Just now';
  } else if (diffDays === 0) {
    label = `Today at ${formatHeaderTimeLabel(due)}`;
  } else if (diffDays === 1) {
    label = `Tomorrow at ${formatHeaderTimeLabel(due)}`;
  } else if (diffDays === -1) {
    label = `Yesterday at ${formatHeaderTimeLabel(due)}`;
  } else {
    label = `${formatDateNoYear(due)} ${formatHeaderTimeLabel(due)}`;
  }

  const isOverdue = !isTerminal && due.getTime() < now.getTime();
  return { label, isOverdue };
};

const getResolvedHeaderDisplay = (task) => {
  const statusLower = (task?.status || '').toLowerCase();
  if (!['resolved', 'rejected'].includes(statusLower)) return null;
  const ended = task?.resolvedAt || task?.updatedAt || null;
  if (!ended) return null;
  const end = new Date(ended);
  const start = task?.startedAt || task?.createdAt || null;
  let duration = null;
  if (start) {
    duration = formatDuration(start, end);
  }
  const endStr = `${formatDateNoYear(end)} ${formatHeaderTimeLabel(end)}`;
  return { label: statusLower === 'rejected' ? 'Rejected' : 'Resolved', value: duration ? `${endStr} in ${duration}` : endStr };
};

const TaskDetailsModal = ({
  isOpen,
  task,
  onClose,
  onActionClick,
  onCreateSubtask,
  onSubtaskClick,
  onImageClick,
  onEditClick,
  departments = [],
  employees = [],
  employeeLocations = [],
  canModifyTask
}) => {
  const isActive = Boolean(isOpen && task);

  const extractMeetLinkFromText = (text = '') => {
    if (!text || typeof text !== 'string') return null;
    const meetRegex = /(https?:\/\/(?:meet\.google\.com|hangouts\.google\.com|calendar\.google\.com\/event[^\s]*|zoom\.us\/[^\s]+|teams\.microsoft\.com\/[^\s]+)[^\s]*)/i;
    const match = text.match(meetRegex);
    return match ? match[0] : null;
  };

  const renderDescriptionLinks = (text = '') => {
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    const parts = text.split(urlRegex);
    return parts.map((part, idx) => {
      if (urlRegex.test(part)) {
        return (
          <a key={idx} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </a>
        );
      }
      return <span key={idx}>{part}</span>;
    });
  };

  // Lock body scroll when open (must run every render)
  useBodyScrollLock(isActive);

  // Close on ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
    };
    if (isActive) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isActive, onClose]);

  if (!isActive) return null;

  const canModify = typeof canModifyTask === 'function' ? canModifyTask(task) : true;
  const typeLabel = task?.taskType === 'customer' ? 'Customer Request' : (task?.taskType || 'Task');
  const meetingLink =
    task?.meetingLink ||
    task?.meetingURL ||
    task?.meetingUrl ||
    task?.meetUrl ||
    task?.hangoutLink ||
    task?.calendar?.hangoutLink ||
    task?.meeting?.hangoutLink ||
    extractMeetLinkFromText(task?.description || '');

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose && onClose();
  };

  const statusLabel = task.status?.replace('-', ' ');
  const isTerminalStatus = typeof task.status === 'string' && ['resolved', 'rejected'].includes(task.status.toLowerCase());
  const isPending = typeof task.status === 'string' && (task.status.toLowerCase() === 'pending' || task.status.toLowerCase() === 'incoming');
  const completionPercent = Number.isFinite(task.completionPercentage)
    ? Math.max(0, Math.min(100, task.completionPercentage))
    : 0;
  const dueHeader = getDueHeaderDisplay(task);
  const resolvedHeader = getResolvedHeaderDisplay(task);
  const metaChips = [
    { label: 'Customer', value: task.customer?.name || 'N/A' },
    { label: 'Department', value: task.department?.name || 'Not assigned' },
    { label: 'Created', value: formatRelativeTime(task.createdAt) },
    resolvedHeader ? { label: resolvedHeader.label, value: resolvedHeader.value } : null,
    dueHeader ? { label: 'Due', value: dueHeader.isOverdue ? `${dueHeader.label} (Overdue)` : dueHeader.label } : null,
    task.followUpOn ? { label: 'Follow Up', value: formatDateTimeUser(task.followUpOn) } : null
  ].filter(Boolean);

  return (
    <div className="modal-overlay task-modal-overlay" role="dialog" aria-modal="true" aria-label="Task details" onClick={handleOverlayClick}>
      <div className="modal-content task-modal-container" onClick={(e) => e.stopPropagation()}>
        <div
          className="modal-header task-modal-header"
          style={{
            background: `linear-gradient(180deg, ${hexToRgba(getTaskStatusColor(task?.status || ''))} 0%, #ffffff 100%)`
          }}
        >
          <div className="task-modal-header-content">
            <div className="task-title-row">
              <span className="task-id-pill">{typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} #{task.id}</span>
              {statusLabel && (
                <span
                  className={`task-status-pill ${isPending ? 'is-pending' : ''}`}
                  style={{ backgroundColor: getTaskStatusColor(task.status) }}
                >
                  {statusLabel}
                </span>
              )}
              {task.priority && (
                <span className={`task-priority-pill priority-${String(task.priority).toLowerCase()}`}>
                  {task.priority}
                </span>
              )}
            </div>
            {!!metaChips.length && (
              <div className="task-meta-row">
                {metaChips.map((chip) => (
                  <div key={chip.label} className="task-meta-chip">
                    <span className="chip-label">{chip.label}</span>
                    <span className="chip-value">{chip.value}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Progress bar - compact, full width, percent on the side */}
            <div className="completion-progress" style={{ marginTop: 6 }} aria-label={`Completion ${completionPercent}%`}>
              <div
                className="progress-bar"
                role="progressbar"
                aria-valuenow={completionPercent}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div className="progress-fill" style={{ width: `${completionPercent}%` }} />
              </div>
              <span className="progress-text" style={{ fontSize: 12 }}>{completionPercent}%</span>
            </div>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close" title="Close">Close</button>
        </div>
        <div className="modal-body task-modal-body">
          <div className="form-row">
            <div className="form-group">
              <label>Description</label>
              <div className="task-description">
                {task.description ? renderDescriptionLinks(task.description) : 'No description provided'}
              </div>
              {typeof task?.taskType === 'string' && task.taskType.toLowerCase() === 'meeting' && meetingLink && (
                <div className="task-description" style={{ marginTop: 6 }}>
                  <strong>Meeting:</strong>{' '}
                  <a href={meetingLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>
                    <strong>Join</strong>
                  </a>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>People & Timing</label>
              <div className="combined-inputs three-cols">
                <div className="mini-field">
                  <label>Field Staff</label>
                  <div className="detail-value">{task.assignedEmployee ? `${task.assignedEmployee.firstName} ${task.assignedEmployee.lastName}` : 'Unassigned'}</div>
                </div>
                <div className="mini-field">
                  <label>Supervisor</label>
                  <div className="detail-value">{task.supervisor ? `${task.supervisor.firstName} ${task.supervisor.lastName}` : 'Unassigned'}</div>
                </div>
                <div className="mini-field">
                  <label>Due Date</label>
                  <div className="detail-value">{task.dueDate ? formatDateTimeNoYear(task.dueDate) : '-'}</div>
                </div>
              </div>
            </div>
          </div>

        {/*   <div className="form-row">
            <div className="form-group">
              <label>Customer</label>
              <div className="detail-value">{task.customer?.name || 'N/A'}</div>
            </div>
            <div className="form-group">
              <label>Department</label>
              <div className="detail-value">{task.department?.name || 'Not assigned'}</div>
            </div>
          </div> */}

          {/* Location Details */}
          <div className="form-row">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Location</label>
              {task.location && task.location.coordinates && task.location.coordinates.lat && task.location.coordinates.lng ? (
                <div className="detail-value">
                  <div className="map-embed-container">
                    <iframe
                      title="Task Location Map"
                      src={`https://maps.google.com/maps?q=${task.location.coordinates.lat},${task.location.coordinates.lng}&z=15&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                    <div className="map-overlay-glass">
                      <div className="overlay-line">{task.location.address || 'Unknown location'}</div>
                      <a
                        className="overlay-link"
                        href={`https://www.google.com/maps?q=${task.location.coordinates.lat},${task.location.coordinates.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in Maps
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="detail-value">No location</div>
              )}
            </div>
          </div>


          {task.subtasks && task.subtasks.length > 0 && (
            <div className="form-row">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Subtasks ({task.subtasks.length})</label>
                <div className="subtasks-list task-subtasks-list">
                  {task.subtasks.map((subtask, index) => (
                    <div
                      key={subtask.id}
                      className="subtask-item task-subtask-card clickable"
                      onClick={() => onSubtaskClick && onSubtaskClick(subtask)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="subtask-header">
                        <span className="subtask-number">#{index + 1}</span>
                        <span className="subtask-description">{subtask.description}</span>
                        <span
                          className="subtask-status"
                          style={{ backgroundColor: getTaskStatusColor(subtask.status) }}
                        >
                          {subtask.status?.replace('-', ' ')}
                        </span>
                        {subtask.priority && (
                          <span className={`subtask-priority priority-badge priority-${subtask.priority.toLowerCase()}`}>
                            {subtask.priority}
                          </span>
                        )}
                      </div>
                      <div className="subtask-meta">
                        <span><strong>Department:</strong> {subtask.department?.name || 'N/A'}</span>
                        {subtask.dueDate && (
                          <span><strong>Due:</strong> {formatDateNoYear(subtask.dueDate)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="form-row">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Timeline</label>
              {(() => {
                const created = task.createdAt ? new Date(task.createdAt) : null;
                const started = task.startedAt ? new Date(task.startedAt) : null;
                const resolved = task.resolvedAt ? new Date(task.resolvedAt) : null;
                const end = resolved || new Date();
                const start = created || end;
                const range = Math.max(1, end.getTime() - start.getTime());
                const pct = (d) => {
                  if (!d) return 0;
                  return Math.max(0, Math.min(100, ((new Date(d).getTime() - start.getTime()) / range) * 100));
                };
                const createdPct = created ? 0 : 0;
                const startedPct = started ? pct(started) : 0;
                const resolvedPct = pct(end);
                const duration = (started || created) && resolved ? formatDuration(started || created, resolved) : null;
                const statusColor = getTaskStatusColor(task?.status || '');
                const statusGradientEnd = lightenHex(statusColor, 0.35);
                const createdEndPct = started ? startedPct : resolvedPct;
                const startedSegWidth = started ? Math.max(0, resolvedPct - startedPct) : 0;
                const resolvedSegLeft = Math.max(0, resolvedPct - 2); // show a small green tip
                const resolvedSegWidth = resolved ? Math.min(2, resolvedPct) : 0;
                return (
                  <div className="task-timeline">
                    <div className="timeline-bar" aria-label="Task timeline">
                      {/* Created -> Started */}
                      <div
                        className="timeline-fill created"
                        style={{
                          left: '0%',
                          width: `${createdEndPct}%`
                        }}
                      />
                      {/* Started -> Resolved (or now) */}
                      {started && (
                        <div
                          className="timeline-fill started"
                          style={{
                            left: `${startedPct}%`,
                            width: `${startedSegWidth}%`
                          }}
                        />
                      )}
                      {/* Resolved tip */}
                      {resolved && (
                        <div
                          className="timeline-fill resolved"
                          style={{
                            left: `calc(${resolvedSegLeft}% )`,
                            width: `${resolvedSegWidth}%`
                          }}
                        />
                      )}
                      <div className="timeline-marker created" style={{ left: `${createdPct}%` }} title={`Created: ${created ? formatDateTimeNoYear(created) : '-'}`} />
                      {started && <div className="timeline-marker started" style={{ left: `${startedPct}%` }} title={`Started: ${formatDateTimeNoYear(started)}`} />}
                      {resolved && <div className="timeline-marker resolved" style={{ left: `calc(${resolvedPct}% - 8px)` }} title={`Resolved: ${formatDateTimeNoYear(resolved)}`} />}
                    </div>
                    <div className="timeline-labels">
                      <div className="timeline-label"><strong>Created:</strong> {created ? formatDateTimeNoYear(created) : '-'}</div>
                      {started && <div className="timeline-label"><strong>Started:</strong> {formatDateTimeNoYear(started)}</div>}
                      {resolved && <div className="timeline-label"><strong>Resolved:</strong> {formatDateTimeNoYear(resolved)}</div>}
                      {duration && <div className="timeline-label"><strong>Time taken:</strong> {duration}</div>}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {(task.images?.length > 0 || task.employeeimages?.length > 0) && (
            <div className="form-row">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Images</label>
                <div className="image-grid task-images-grid">
                  {[...(task.images || []), ...(task.employeeimages || [])].map((img, idx) => (
                    <img key={idx} src={img} alt={`Task media ${idx + 1}`} className="task-image" onClick={() => onImageClick && onImageClick(img)} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer task-modal-footer">
          {canModify && !isTerminalStatus && (
            <>
              <button className="btn btn-secondary" onClick={() => onEditClick && onEditClick(task)}>Edit</button>
              <button
                className={`action-button ${isPending ? 'start' : 'resolve'}`}
                onClick={() => onActionClick && onActionClick(task, 'resolve')}
              >
                {isPending ? 'Start' : 'Complete'}
              </button>
              <button className="action-button reject" onClick={() => onActionClick && onActionClick(task, 'reject')}>Reject</button>
              <button className="btn btn-secondary" onClick={() => onCreateSubtask && onCreateSubtask(task)}>Add Subtask</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsModal;


